import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Bell, FileText, AlertTriangle, MessageCircle, Check, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import NotificationBadge from './NotificationBadge';

const NotificationCenter = ({ notifications, unreadCount, onMarkAsRead, onMarkAllAsRead, onClearAll }) => {
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'alert':
        return <AlertTriangle className="w-5 h-5 text-yellow-400" />;
      case 'document':
        return <FileText className="w-5 h-5 text-purple-400" />;
      case 'mention':
        return <MessageCircle className="w-5 h-5 text-blue-400" />;
      default:
        return <Bell className="w-5 h-5 text-gray-400" />;
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high':
        return 'text-red-400 bg-red-500/10 border-red-500/30';
      case 'medium':
        return 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30';
      case 'low':
        return 'text-green-400 bg-green-500/10 border-green-500/30';
      default:
        return 'text-gray-400 bg-gray-500/10 border-gray-500/30';
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Ahora';
    if (diffMins < 60) return `Hace ${diffMins}m`;
    if (diffHours < 24) return `Hace ${diffHours}h`;
    if (diffDays < 7) return `Hace ${diffDays}d`;
    return date.toLocaleDateString();
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="relative text-gray-400 hover:text-white">
          <Bell className="w-5 h-5" />
          <NotificationBadge count={unreadCount} className="notification-badge-absolute" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="glass-strong w-[380px] p-0 border-purple-500/30" align="end">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-purple-400" />
            <h3 className="font-semibold text-white">Notificaciones</h3>
            {unreadCount > 0 && (
              <Badge variant="outline" className="bg-purple-500/20 text-purple-300 border-purple-500/30">
                {unreadCount} nueva{unreadCount !== 1 ? 's' : ''}
              </Badge>
            )}
          </div>
          {notifications.length > 0 && (
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onMarkAllAsRead}
                  className="text-xs text-purple-400 hover:text-purple-300"
                >
                  <Check className="w-4 h-4 mr-1" />
                  Marcar todo
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearAll}
                className="text-xs text-gray-400 hover:text-white"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>

        {/* Notifications List */}
        <ScrollArea className="h-[400px]">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-4">
              <Bell className="w-12 h-12 text-gray-600 mb-3" />
              <p className="text-gray-400 text-sm text-center">
                No hay notificaciones
              </p>
              <p className="text-gray-500 text-xs text-center mt-1">
                Te notificaremos cuando haya actualizaciones
              </p>
            </div>
          ) : (
            <div className="p-2">
              <AnimatePresence>
                {notifications.map((notification) => (
                  <motion.div
                    key={notification.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    className={`p-3 mb-2 rounded-lg border cursor-pointer transition-all ${
                      notification.read
                        ? 'bg-white/5 border-white/10'
                        : 'bg-purple-500/10 border-purple-500/30'
                    } hover:bg-white/10`}
                    onClick={() => onMarkAsRead(notification.id)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-1">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <p className="text-sm font-medium text-white truncate">
                            {notification.title}
                          </p>
                          {notification.severity && (
                            <Badge
                              variant="outline"
                              className={`text-xs ${getSeverityColor(notification.severity)}`}
                            >
                              {notification.severity}
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-gray-400 line-clamp-2 mb-1">
                          {notification.message}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatTimestamp(notification.timestamp)}
                        </p>
                      </div>
                      {!notification.read && (
                        <div className="flex-shrink-0">
                          <div className="w-2 h-2 rounded-full bg-purple-500 pulse-animation" />
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationCenter;
